import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  broadcastInputSchema,
  type BroadcastInput,
  type NotificationChannel,
} from '@pirouette/shared';
import { useBroadcasts, useCreateBroadcast } from '@/data/messaging';
import { useClasses } from '@/data/classes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/broadcasts')({
  component: BroadcastsPage,
});

const ALL_CHANNELS: NotificationChannel[] = ['email', 'sms', 'push', 'in_app'];

function BroadcastsPage() {
  const broadcasts = useBroadcasts();
  const createBroadcast = useCreateBroadcast();
  const classes = useClasses();
  const [scope, setScope] = useState<'all' | 'class' | 'comp'>('all');
  const [channels, setChannels] = useState<NotificationChannel[]>(['email', 'in_app']);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<BroadcastInput>({
    resolver: zodResolver(broadcastInputSchema),
    mode: 'onBlur',
    defaultValues: {
      scope: 'all',
      title: '',
      body: '',
      channels: ['email', 'in_app'],
    },
  });

  async function onSubmit(values: BroadcastInput) {
    try {
      await createBroadcast.mutateAsync(values);
      toast({
        title: 'Broadcast queued',
        description: 'It will go out to recipients matching the scope.',
        variant: 'success',
      });
      reset();
      // Mirror the RHF reset into local UI state — the `channels` and
      // `scope` pieces are kept outside RHF for the checkbox/radio group
      // ergonomics, so reset() alone leaves them with the just-submitted
      // values while the form internals revert to defaults.
      setChannels(['email', 'in_app']);
      setScope('all');
    } catch (err) {
      toast({
        title: "Couldn't queue broadcast",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  function toggleChannel(c: NotificationChannel) {
    const next = channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c];
    setChannels(next);
    setValue('channels', next, { shouldValidate: true });
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Announcements
        </p>
        <h1 className="mt-2">Broadcasts</h1>
      </header>

      <section aria-labelledby="new-broadcast" className="surface-elevated p-6">
        <h2 id="new-broadcast" className="font-medium text-[var(--text-lg)]">
          New broadcast
        </h2>
        <form
          onSubmit={(e) => void handleSubmit(onSubmit)(e)}
          noValidate
          className="mt-5 grid gap-5"
        >
          <fieldset>
            <legend className="mb-2 font-medium text-[var(--text-sm)]">Scope</legend>
            <div role="radiogroup" className="flex flex-wrap gap-2">
              {(['all', 'class', 'comp'] as const).map((s) => (
                <label
                  key={s}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 py-1.5 text-[var(--text-sm)] has-[input:checked]:bg-[var(--color-accent-muted)]"
                >
                  <input
                    type="radio"
                    value={s}
                    checked={scope === s}
                    onChange={() => {
                      setScope(s);
                      setValue('scope', s, { shouldValidate: true });
                      if (s !== 'class') setValue('scope_target_id', null);
                    }}
                  />
                  <span className="capitalize">
                    {s === 'all' ? 'All families' : s === 'class' ? 'A class' : 'Comp team'}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {scope === 'class' && (
            <div>
              <Label htmlFor="scope-target">Class</Label>
              <select
                id="scope-target"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                defaultValue=""
                {...register('scope_target_id')}
              >
                <option value="" disabled>
                  Select a class…
                </option>
                {classes.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.weekday} {c.start_time})
                  </option>
                ))}
              </select>
              {errors.scope_target_id && (
                <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                  {errors.scope_target_id.message}
                </p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="b-title">Title</Label>
            <Input id="b-title" aria-invalid={!!errors.title} {...register('title')} />
            {errors.title && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.title.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="b-body">Message</Label>
            <textarea
              id="b-body"
              rows={6}
              aria-invalid={!!errors.body}
              className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 focus-visible:outline-none"
              {...register('body')}
            />
            {errors.body && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.body.message}
              </p>
            )}
          </div>

          <fieldset>
            <legend className="mb-2 font-medium text-[var(--text-sm)]">Channels</legend>
            <div className="flex flex-wrap gap-2">
              {ALL_CHANNELS.map((c) => (
                <label
                  key={c}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] px-3 py-1.5 text-[var(--text-sm)] has-[input:checked]:bg-[var(--color-accent-muted)]"
                >
                  <input
                    type="checkbox"
                    checked={channels.includes(c)}
                    onChange={() => toggleChannel(c)}
                  />
                  <span className="capitalize">{c.replace('_', '-')}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
              SMS is rate-limited per family and per broadcast — see the runbook.
            </p>
          </fieldset>

          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Queuing…' : 'Queue broadcast'}
          </Button>
        </form>
      </section>

      <section aria-labelledby="recent-broadcasts">
        <h2 id="recent-broadcasts" className="text-[var(--text-xl)]">
          Recent
        </h2>
        {broadcasts.isLoading && <p className="mt-3">Loading…</p>}
        {broadcasts.data?.length === 0 && (
          <p className="mt-3 text-[var(--color-ink-muted)]">No broadcasts yet.</p>
        )}
        {broadcasts.data && broadcasts.data.length > 0 && (
          <ul className="mt-4 grid gap-2">
            {broadcasts.data.map((b) => (
              <li key={b.id} className="surface p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h3 className="font-medium">{b.title}</h3>
                  <p className="text-[var(--color-ink-subtle)] text-[var(--text-xs)] tabular-nums">
                    {new Date(b.created_at).toLocaleString()}
                  </p>
                </div>
                <p className="mt-2 line-clamp-2 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  {b.body}
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Badge tone="accent">{b.scope}</Badge>
                  {b.channels.map((c) => (
                    <Badge key={c} tone="neutral">
                      {c}
                    </Badge>
                  ))}
                  {b.sent_at ? (
                    <Badge tone="success">sent</Badge>
                  ) : (
                    <Badge tone="warn">pending</Badge>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
